/* ============================================================
   GOLDEN STAR JEWELLERY — "CONTACT US ABOUT SIZING" -> LIVE CHAT
   File: assets/gsj-sizing-chat.js

   Opens the store's existing Shopify Inbox chat instead of sending
   the customer to /pages/contact. Chatting from the product page is
   faster for both sides, and Inbox shows the merchant which page the
   visitor is on.

   IT MUST OPEN THE SAME INBOX CHAT THE CUSTOMER ALREADY SEES —
   never a second or different chat surface. Shopify's agent.js ships
   two custom elements, <shopify-chat> and <shopify-agent>, and the
   Inbox app embed emits config for both. On this storefront exactly
   one mounts:

       <shopify-chat view="chat">      <- classic Inbox. This one.

   <shopify-agent> is the AI assistant surface and is DELIBERATELY
   never targeted here. Do not add it to any selector below.

   So rather than guessing at an API, this presses the very button the
   customer would press. Inside <shopify-chat>'s (open) shadow root:

       <button part="activator" class="activator" aria-label="Open chat"
               aria-expanded="false">

   Pressing that opens whatever chat the merchant has configured, in
   the merchant's own view. Only if the button cannot be found does it
   fall back to the element's own show() / open accessor, and then to
   the legacy inbox-chat-loader globals (older storefronts — and what
   `shopify theme dev` happens to serve).

   Note `open` is an ACCESSOR, not a method. An earlier version tested
   typeof el.open === 'function', which is always false, so it silently
   fell through to show(). Hence the explicit ladder below.

   Clicks are dispatched as a full pointer/mouse sequence; a bare
   .click() is ignored by widgets that bind to pointerdown (the same
   thing bit the Nivoda swatches).

   NO MESSAGE PRE-FILL, and there cannot be one. The chat UI renders
   inside a CROSS-ORIGIN iframe:

       shopify-chat #shadow > shopify-agent-runtime > iframe
       src=https://www.northamericagoldenstar.ca/agent/handoff

   The storefront is northamericagoldenstar.ca and the iframe is on the
   www. subdomain, so contentDocument throws and there is no reachable
   input anywhere in the tree. An earlier version polled for a textarea
   16 times on every click and could never have found one. Product
   context reaches the merchant anyway — Inbox shows them the page the
   visitor is on, which is this product.

   THE CUSTOMER NEVER LEAVES THE PRODUCT PAGE. There is no navigation
   anywhere in this file — no /pages/contact redirect, and the CTA is a
   <button>, not a link, so a middle- or cmd-click cannot navigate
   either. The whole point is that they stay on the product they are
   asking about, because that is the context Inbox shows the merchant.

   The old contact-page fallback existed for "the widget did not open".
   That is now handled by waiting instead: the usual cause is simply
   that agent.js has not mounted yet, so this retries for up to 8s and
   opens the moment the widget is available.

   Retries never re-press the activator — pressing it again would TOGGLE
   an already-open chat shut. After the first press, retries use the
   idempotent `open = true` setter instead, and the loop stops as soon
   as the widget reports itself open, so a customer who opens and then
   deliberately closes the chat does not have it reopened under them.
   ============================================================ */
(function () {
  'use strict';

  var SELECTOR      = '[data-gsj-sizing-chat]';
  var MAX_WAIT_MS   = 8000;   /* how long to keep waiting for the widget */
  var RETRY_EVERY   = 300;

  /* Inbox only. Adding 'shopify-agent' here would open the AI assistant
     instead of the store's real chat — that is the bug this guards. */
  function inboxHost() {
    return document.querySelector('shopify-chat');
  }

  function activatorIn(host) {
    if (!host || !host.shadowRoot) return null;
    return host.shadowRoot.querySelector(
      'button[part="activator"], button.activator, [part="activator"]'
    );
  }

  function press(el) {
    var types = ['pointerdown', 'mousedown', 'pointerup', 'mouseup', 'click'];
    for (var i = 0; i < types.length; i++) {
      var type = types[i];
      try {
        var ev;
        if (type.indexOf('pointer') === 0 && typeof window.PointerEvent === 'function') {
          ev = new PointerEvent(type, { bubbles: true, cancelable: true, view: window });
        } else {
          ev = new MouseEvent(type, { bubbles: true, cancelable: true, view: window });
        }
        el.dispatchEvent(ev);
      } catch (e) { /* a later type may still land */ }
    }
  }

  function isPanelSized(el) {
    var r = el.getBoundingClientRect();
    return r.width > 200 && r.height > 200;
  }

  /* Legacy widget only (no <shopify-chat> element). Looks for a real panel.

     This must NEVER be used against <shopify-chat>: that host element
     measures ~840x987 even while the chat is CLOSED, so a size heuristic
     reports "already open" and the opener then does nothing at all. That
     exact bug shipped once — the host's own `open` property is the only
     trustworthy signal, see isOpen(). */
  function legacyLooksOpen() {
    var i;
    var frames = document.querySelectorAll('iframe');
    for (i = 0; i < frames.length; i++) {
      var f = frames[i];
      if (/inbox|shopify-chat|ShopifyChat/i.test((f.src || '') + f.id + (f.className || '')) &&
          isPanelSized(f)) {
        return true;
      }
    }
    var legacy = document.querySelectorAll('#ShopifyChat, [id*="ShopifyChat"]');
    for (i = 0; i < legacy.length; i++) {
      if (isPanelSized(legacy[i])) return true;
    }
    return false;
  }

  /* The widget's own answer, preferred over any guesswork. */
  function isOpen() {
    var host = inboxHost();
    if (host) {
      /* Authoritative, and the ONLY safe test for this element — the host is
         panel-sized whether open or shut. Measured on the live storefront:
         840x987 with the chat closed. */
      try { if (typeof host.open === 'boolean') return host.open === true; } catch (e) {}
      /* No such property (older build): the activator answers instead. It is
         not always present in the shadow root, so absence means "not open". */
      var act = activatorIn(host);
      return !!(act && act.getAttribute('aria-expanded') === 'true');
    }
    return legacyLooksOpen();
  }

  function setOpen(host) {
    /* Idempotent: setting true on an already-open chat is a no-op, which is
       exactly why retries use this rather than pressing the toggle again. */
    try { if ('open' in host) { host.open = true; return true; } } catch (e) {}
    try { if (typeof host.show === 'function') { host.show(); return true; } } catch (e) {}
    return false;
  }

  function legacyOpen() {
    var api = window.ShopifyChat;
    if (api) {
      var names = ['open', 'show', 'openChat', 'expand'];
      for (var i = 0; i < names.length; i++) {
        try {
          if (typeof api[names[i]] === 'function') { api[names[i]](); return true; }
        } catch (e) {}
      }
    }
    var btn = document.querySelector(
      '#ShopifyChat button, [id*="ShopifyChat"] button, #ShopifyChat, [id*="ShopifyChat"]'
    );
    if (btn) { press(btn); return true; }
    return false;
  }

  function openChat() {
    var deadline = Date.now() + MAX_WAIT_MS;
    var pressed = false, done = false;

    function tick() {
      if (done) return;

      if (isOpen()) { done = true; return; }   /* success — stop, never reopen */

      var host = inboxHost();
      if (host) {
        if (!pressed) {
          /* First attempt: the customer's own button, so the merchant's own
             view opens exactly as if they had clicked the bubble. */
          var act = activatorIn(host);
          if (act) { press(act); } else { setOpen(host); }
          pressed = true;
        } else {
          setOpen(host);
        }
      } else {
        legacyOpen();                          /* widget not mounted yet, or legacy */
      }

      if (Date.now() < deadline) {
        window.setTimeout(tick, RETRY_EVERY);
      } else {
        done = true;                           /* give up quietly — stay on the page */
      }
    }

    tick();
  }

  function onClick(event) {
    var trigger;
    try {
      trigger = event.target && event.target.closest ? event.target.closest(SELECTOR) : null;
    } catch (e) { return; }
    if (!trigger) return;

    /* No navigation, ever: stop the click here whatever the element is. */
    event.preventDefault();

    try { openChat(); } catch (e) { /* never surface */ }
  }

  try {
    document.addEventListener('click', onClick);
  } catch (e) { /* the link still works on its own */ }
})();
